import type { ChangeEvent, FC } from "react";

import { TextArea } from "@ui/components/Inputs/TextArea";
import classNames from "classnames";
import DOMPurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";

import styles from "./ContentFeedbackForm.module.scss";

const sanitize = DOMPurify.sanitize;

type ContentFeedbackVariant = "default" | "status" | "submitted" | "comment" | "support";

type ContentFeedbackAction = {
  label: string;
  color?: "primary" | "secondary";
  onClick?: () => void;
  href?: string;
};

type ContentFeedbackCommentProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

type ContentFeedbackFormProps = {
  title?: string;
  descriptionHtml?: string;
  helpDescriptionHtml?: string;
  actions?: ContentFeedbackAction[];
  hasPrimaryAction?: boolean;
  hasTextField?: boolean;
  hasHelpSection?: boolean;
  commentProps?: ContentFeedbackCommentProps;
  variant?: ContentFeedbackVariant;
};

type ContentFeedbackTitleProps = {
  text?: string;
  descriptionHtml?: string;
};

const ContentFeedbackTitleAndDescription: FC<ContentFeedbackTitleProps> = ({ text, descriptionHtml }) => {
  if (!text && !descriptionHtml) {
    return null;
  }

  return (
    <>
      {text ? <div className={styles.titleText}>{text}</div> : null}
      {descriptionHtml ? (
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitize(descriptionHtml) }} />
      ) : null}
    </>
  );
};

const getActionColor = (
  action: ContentFeedbackAction,
  index: number,
  actionsLength: number,
  hasPrimaryAction: boolean,
  variant: ContentFeedbackVariant
): ContentFeedbackAction["color"] => {
  if (variant === "comment") {
    return "primary";
  }

  if (hasPrimaryAction && index === actionsLength - 1) {
    return "primary";
  }

  return action.color ?? "secondary";
};

const renderActionButton = (
  action: ContentFeedbackAction,
  index: number,
  actionsLength: number,
  hasPrimaryAction: boolean,
  variant: ContentFeedbackVariant
): JSX.Element => {
  const { label, href, onClick } = action;
  const buttonActionProps = "href" in action ? { href } : { onClick };

  return (
    <Button
      key={`${label}-${index}`}
      variant={getActionColor(action, index, actionsLength, hasPrimaryAction, variant)}
      size="md"
      {...buttonActionProps}
    >
      {label}
    </Button>
  );
};

const renderActions = (
  actions: ContentFeedbackAction[],
  hasPrimaryAction: boolean,
  variant: ContentFeedbackVariant,
  className?: string
): JSX.Element | null => {
  if (!actions.length) {
    return null;
  }

  return (
    <div className={className ?? styles.buttonsWrapper}>
      {actions.map((action, index) => renderActionButton(action, index, actions.length, hasPrimaryAction, variant))}
    </div>
  );
};

export const ContentFeedbackForm: FC<ContentFeedbackFormProps> = ({
  title,
  descriptionHtml,
  actions = [],
  hasPrimaryAction = false,
  hasTextField = true,
  hasHelpSection = true,
  helpDescriptionHtml,
  commentProps,
  variant = "default",
}) => {
  if (variant === "submitted") {
    return (
      <div className={classNames(styles.container, styles.commentVariant)}>
        <ContentFeedbackTitleAndDescription text={title} descriptionHtml={descriptionHtml} />
      </div>
    );
  }

  if (variant === "status") {
    return (
      <div className={styles.container}>
        <ContentFeedbackTitleAndDescription text={title} />
      </div>
    );
  }

  if (variant === "support") {
    return (
      <div className={styles.container}>
        <ContentFeedbackTitleAndDescription descriptionHtml={descriptionHtml} />
      </div>
    );
  }

  if (variant === "comment") {
    return (
      <div className={classNames(styles.container, styles.commentVariant)}>
        <div className={styles.copyBlock}>
          <ContentFeedbackTitleAndDescription text={title} descriptionHtml={descriptionHtml} />
        </div>
        {hasTextField && (
          <TextArea
            id="faq-feedback-comment"
            label={commentProps?.label}
            placeholder={commentProps?.placeholder}
            value={commentProps?.value}
            onChange={commentProps?.onChange}
          />
        )}
        {hasTextField ? renderActions(actions, hasPrimaryAction, variant) : null}
        {hasHelpSection ? <ContentFeedbackTitleAndDescription descriptionHtml={helpDescriptionHtml} /> : null}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ContentFeedbackTitleAndDescription text={title} />
      {renderActions(actions, hasPrimaryAction, variant, styles.buttonsWrapper)}
    </div>
  );
};
